import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Modal } from '../common';
import { authApi } from '../../services/authApi';
import { readStoredUser } from '../../utils/navbarUser';
import './FollowersPopup.css';

function FollowersPopup({ isOpen, onClose, username, initialTab = 'followers' }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followStates, setFollowStates] = useState({});
  const [loading, setLoading] = useState(true);
  const currentUser = readStoredUser();
  const currentUsername = currentUser?.username || null;

  const fetchData = useCallback(async () => {
    if (!isOpen || !username) return;

    setLoading(true);
    try {
      const [followersData, followingData] = await Promise.all([
        authApi.getFollowersList(username),
        authApi.getFollowingList(username),
      ]);

      const nextFollowers = followersData.followers || [];
      const nextFollowing = followingData.following || [];
      setFollowers(nextFollowers);
      setFollowing(nextFollowing);

      if (currentUsername) {
        const usernames = new Set([
          ...nextFollowers.map((entry) => entry.username),
          ...nextFollowing.map((entry) => entry.username),
        ]);
        const states = {};

        await Promise.all(
          [...usernames].map(async (targetUsername) => {
            if (!targetUsername || targetUsername === currentUsername) return;
            try {
              const result = await authApi.checkIsFollowing(targetUsername);
              states[targetUsername] = Boolean(result.isFollowing);
            } catch {
              states[targetUsername] = false;
            }
          })
        );

        setFollowStates(states);
      } else {
        setFollowStates({});
      }
    } catch (error) {
      console.error('Failed to load followers/following:', error);
      setFollowers([]);
      setFollowing([]);
      setFollowStates({});
    } finally {
      setLoading(false);
    }
  }, [currentUsername, isOpen, username]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleToggleFollow = async (targetUsername) => {
    const isCurrentlyFollowing = Boolean(followStates[targetUsername]);

    try {
      if (isCurrentlyFollowing) {
        await authApi.unfollowUser(targetUsername);
      } else {
        await authApi.followUser(targetUsername);
      }

      setFollowStates((prev) => ({
        ...prev,
        [targetUsername]: !isCurrentlyFollowing,
      }));
    } catch (error) {
      console.error('Follow toggle failed:', error);
    }
  };

  const handleUserClick = (targetUsername) => {
    onClose();
    navigate(`/profile/${targetUsername}`);
  };

  const list = activeTab === 'followers' ? followers : following;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" className="followers-popup__modal">
      <div className="followers-popup">
        <div className="followers-popup__tabs">
          <button
            type="button"
            className={`followers-popup__tab ${activeTab === 'followers' ? 'followers-popup__tab--active' : ''}`}
            onClick={() => setActiveTab('followers')}
          >
            Followers ({followers.length})
          </button>
          <button
            type="button"
            className={`followers-popup__tab ${activeTab === 'following' ? 'followers-popup__tab--active' : ''}`}
            onClick={() => setActiveTab('following')}
          >
            Following ({following.length})
          </button>
        </div>

        <div className="followers-popup__content">
          {loading ? (
            <div className="followers-popup__loading">Loading...</div>
          ) : list.length === 0 ? (
            <div className="followers-popup__empty">
              {activeTab === 'followers' ? 'No followers yet' : 'Not following anyone yet'}
            </div>
          ) : (
            <ul className="followers-popup__list">
              {list.map((entry) => (
                <li key={entry.id || entry.username} className="followers-popup__row">
                  <button
                    type="button"
                    className="followers-popup__user-btn"
                    onClick={() => handleUserClick(entry.username)}
                  >
                    <div className="followers-popup__avatar">
                      {entry.profileImage ? (
                        <img src={entry.profileImage} alt="" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="followers-popup__avatar-placeholder">
                          {(entry.username || '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="followers-popup__info">
                      <span className="followers-popup__username">{entry.username}</span>
                      {(entry.firstName || entry.lastName) && (
                        <span className="followers-popup__realname">
                          {entry.firstName || ''} {entry.lastName || ''}
                        </span>
                      )}
                    </div>
                  </button>
                  {currentUsername && entry.username !== currentUsername && (
                    <button
                      type="button"
                      className={`followers-popup__follow-btn ${followStates[entry.username] ? 'followers-popup__follow-btn--following' : ''}`}
                      onClick={() => handleToggleFollow(entry.username)}
                    >
                      {followStates[entry.username] ? 'Following' : 'Follow'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}

FollowersPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  username: PropTypes.string.isRequired,
  initialTab: PropTypes.oneOf(['followers', 'following']),
};

export default FollowersPopup;
